<script setup lang="ts">
import { computed } from 'vue';
import { useTranslation } from 'i18next-vue';
import { WorkProgressDto } from '@/api/generator';
import { getProgressStateIcon, WorkProgressKey } from '../utils/jobExecutionProgress';
import Tooltip from '@/components/Tooltip/Tooltip.vue';

const { t } = useTranslation();

const props = withDefaults(
  defineProps<{
    value?: WorkProgressDto;
    background?: boolean;
  }>(),
  {
    value: undefined,
    background: () => false,
  },
);

const order: WorkProgressKey[] = [
  'passedSteps',
  'warningSteps',
  'failedSteps',
  'withoutEvaluation',
  'skippedSteps',
  'openSteps',
];

const progress = computed(() =>
  order.map((key) => ({
    key: key,
    count: props.value?.[key] ?? 0,
    iconProps: getProgressStateIcon(key),
  })),
);

const getTooltipText = (key: WorkProgressKey): string => {
  switch (key) {
    case 'passedSteps':
      return t('Progress.PassedWorkstep');
    case 'warningSteps':
      return t('Progress.WorkstepWithWarning');
    case 'failedSteps':
      return t('Progress.FailedWorkstep');
    case 'withoutEvaluation':
      return t('Progress.WorkstepWithoutEvaluation');
    case 'skippedSteps':
      return t('Progress.SkippedWorkstep');
    case 'openSteps':
      return t('Progress.OpenWorksteps');
    default:
      return '';
  }
};
</script>

<template>
  <div class="d-flex flex-wrap">
    <template v-for="state in progress" :key="state.key">
      <v-sheet
        v-if="state.count"
        :rounded="false"
        :color="background ? state.iconProps.color : '#ffffff00'"
        class="d-inline text-center pa-1"
      >
        <Tooltip position="top">
          <template #target="{ props: targetProps }">
            <div class="d-flex align-center" v-bind="targetProps">
              <v-icon v-bind="state.iconProps" class="mr-1" />
              <span>{{ state.count }}</span>
            </div>
          </template>
          <template #tooltip>
            {{ getTooltipText(state.key) }}
          </template>
        </Tooltip>
      </v-sheet>
    </template>
  </div>
</template>
